// Every lift with a SAFed supplementary test that needs the office (Session BDB, lifecycle step B): recommended, due, authorised,
// booked or failed. A lift whose tests are only "not assessed" is never listed, and never shown as overdue. Filter by customer, site
// or where it stands. Reads /api/safed-flow/due. Nothing here writes.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { RaiseSafedButton } from "./RaiseSafedButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { SAFED_STATE_LABEL_VIEW, type SafedAttentionItemView, type SafedLifecycleStateView } from "../../lib/safedFlowTypes";

const STATES: SafedLifecycleStateView[] = ["recommended", "due", "authorised", "booked", "failed"];
const selectCls = "min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 text-sm text-ele-strong";

export function SafedAttentionSection() {
  const [items, setItems] = useState<SafedAttentionItemView[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [state, setState] = useState<SafedLifecycleStateView | "">("");
  const [reloadKey, setReloadKey] = useState(0);
  // Below md the lifts render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  useEffect(() => {
    let live = true;
    try {
      api.safedFlow
        .due({})
        .then((r) => {
          if (!live) return;
          setItems(r.items);
          setTruncated(r.truncated);
        })
        .catch(() => {
          if (live) setFailed(true);
        });
    } catch {
      setFailed(true);
    }
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const customers = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items ?? []) if (i.customerId) m.set(i.customerId, i.customerName ?? i.customerId);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  const sites = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of items ?? []) if (i.siteId && (!customerId || i.customerId === customerId)) m.set(i.siteId, i.siteName ?? i.siteId);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items, customerId]);

  const shown = (items ?? [])
    .filter((i) => !customerId || i.customerId === customerId)
    .filter((i) => !siteId || i.siteId === siteId)
    .map((i) => ({ ...i, tests: i.tests.filter((t) => !state || t.state === state) }))
    .filter((i) => i.tests.length > 0);

  if (failed) return <p role="alert" className="mt-4 text-sm text-ele-muted">Could not load the SAFed list just now.</p>;
  if (!items) return null;

  return (
    <section aria-label="SAFed tests that need you" className="mt-6">
      <h2 className="font-heading text-lg text-ele-strong">SAFed tests that need you</h2>
      <p className="text-xs text-ele-muted"><Link className="text-ele-blue hover:underline" to="/office/safed">Open the full SAFed tests list</Link></p>
      <p className="text-xs text-ele-muted">Recommended, due, authorised, booked or failed. A lift with nothing recorded is not assessed and is not listed.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <label className="text-sm text-ele-body">
          <span className="sr-only">Customer</span>
          <select className={selectCls} value={customerId} onChange={(e) => { setCustomerId(e.target.value); setSiteId(""); }}>
            <option value="">All customers</option>
            {customers.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-ele-body">
          <span className="sr-only">Site</span>
          <select className={selectCls} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
            <option value="">All sites</option>
            {sites.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-ele-body">
          <span className="sr-only">Where it stands</span>
          <select className={selectCls} value={state} onChange={(e) => setState(e.target.value as SafedLifecycleStateView | "")}>
            <option value="">Any state</option>
            {STATES.map((s) => (
              <option key={s} value={s}>{SAFED_STATE_LABEL_VIEW[s]}</option>
            ))}
          </select>
        </label>
      </div>
      {shown.length === 0 ? (
        <p className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">No SAFed tests need you{customerId || siteId || state ? " with those filters" : ""}.</p>
      ) : mobileCards ? (
        /* Below md the four-column table pushes Raise SAFed test past the screen edge. Each lift is
           a card: the lift leads, the site and customer follow, every test stands on its own line
           and the raise control sits on its own 44px row. The card opens the lift's SAFed panel. */
        <MobileCardList label="Lifts with SAFed tests that need you">
          {shown.map((i) => (
            <MobileListCard
              key={i.assetId}
              to={`/office/lifts/${i.assetId}#safed`}
              primary={i.liftRef ?? "Lift"}
              secondary={`${i.siteName} · ${i.customerName}`}
              meta={i.tests.map((t) => (
                <span key={t.testCode} className="block">
                  {t.testCode} {SAFED_STATE_LABEL_VIEW[t.state]}
                </span>
              ))}
              actions={<RaiseSafedButton assetId={i.assetId} compact onRaised={() => setReloadKey((k) => k + 1)} />}
            />
          ))}
        </MobileCardList>
      ) : (
        <table className="ele-list-table mt-3 w-full text-sm">
          <thead>
            <tr className="text-left text-ele-muted">
              <th>Lift</th>
              <th>Site and customer</th>
              <th>Tests</th>
              <th><span className="sr-only">Raise</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.assetId}>
                <td><Link className="text-ele-blue hover:underline" to={`/office/lifts/${i.assetId}#safed`}>{i.liftRef ?? "Lift"}</Link></td>
                <td>{i.siteName} · {i.customerName}</td>
                <td>{i.tests.map((t) => `${t.testCode} ${SAFED_STATE_LABEL_VIEW[t.state]}`).join("; ")}</td>
                <td><RaiseSafedButton assetId={i.assetId} compact onRaised={() => setReloadKey((k) => k + 1)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {truncated && <p className="mt-2 text-xs text-ele-muted">Only the first lifts are shown. Filter by customer or site to see the rest.</p>}
    </section>
  );
}
